// menuLogic.ts — منطق بناء/ترتيب/تصفية قائمة التنقّل (منقول بلا تغيير سلوكيّ من
// frontend/src/components/Sidebar.js). دوالّ خالصة (لا React) لسهولة الاختبار.
import { readStorage } from '../lib/storage';
import type { Permission } from '../lib/accessControl';

export interface MenuItem {
	menu_id?: number;
	permission_key?: string;
	text_key?: string;
	icon_key?: string;
	path?: string;
	route?: string;
	sort_order?: number;
	is_active?: number;
	[key: string]: unknown;
}

const PERM_TO_MENU: Record<string, { path: string; text_key: string; icon_key?: string }> = {
	home: { path: '/home', icon_key: 'home', text_key: 'home' },
	statistics: { path: '/statistics', icon_key: 'statistics', text_key: 'statistics' },
	horses: { path: '/horse-list', text_key: 'horse-list' },
	competitions: { path: '/competitions', icon_key: 'competitions', text_key: 'competitions' },
	accommodation: { path: '/available-rooms', icon_key: 'available-rooms', text_key: 'available-rooms' },
	vetrecords: { path: '/record-list', text_key: 'recordlist' },
	farrierrecord: { path: '/farrier-list', text_key: 'farrierlist' },
	farrierlist: { path: '/farrier-list', text_key: 'farrierlist' },
	'farrier-list': { path: '/farrier-list', text_key: 'farrierlist' },
	reproductionpage: { path: '/reproduction-page', text_key: 'reproductionpage' },
	horsetransportreportpage: {
		path: '/horse-transport-report',
		icon_key: 'horse-transport-report-page',
		text_key: 'horsetransportreportpage',
	},
	accountantreview: { path: '/accountant-review', text_key: 'accountantreview' },
	checkup: { path: '/vet-checkup-view', icon_key: 'checkup', text_key: 'checkup' },
	settings: { path: '/settings', icon_key: 'settings', text_key: 'settings' },
	'my-horses': { path: '/my-horses', icon_key: 'horses', text_key: 'client_horses' },
	myhorses: { path: '/my-horses', icon_key: 'horses', text_key: 'client_horses' },
	'my-horse-stats': { path: '/my-horse-stats', icon_key: 'statistics', text_key: 'client_horse_stats' },
	myhorsestats: { path: '/my-horse-stats', icon_key: 'statistics', text_key: 'client_horse_stats' },
};

export function buildMenuFromPermissions(permissions: Permission[] = []): MenuItem[] {
	const out: MenuItem[] = [];
	const seen = new Set<string>();
	for (const p of permissions || []) {
		if (Number(p?.can_access) !== 1) continue;
		const key = String(p.page_name || p.permission_key || '').toLowerCase();
		const cfg = PERM_TO_MENU[key];
		if (!cfg) continue;
		if (seen.has(cfg.path)) continue;
		seen.add(cfg.path);
		out.push({ permission_key: key, text_key: cfg.text_key || key, icon_key: cfg.icon_key || key, path: cfg.path });
	}
	if (!out.some((i) => i.path === '/home')) {
		out.unshift({ permission_key: 'home', text_key: 'home', icon_key: 'home', path: '/home' });
	}
	return out;
}

export function isHorseOwnerAccount(): boolean {
	const ownerId = readStorage('owner_id');
	if (!ownerId || ownerId === 'null' || ownerId === 'undefined') return false;
	const roleId = Number(readStorage('role_id') || 0);
	const isSystemAdmin = Number(readStorage('is_system_admin') || 0) === 1;
	return roleId === 3 || isSystemAdmin;
}

export const normalizeMenuPath = (value = ''): string =>
	('/' + String(value || '').trim())
		.replace(/^\/+/, '/')
		.replace(/\/+/g, '/')
		.toLowerCase();

const LEGACY_ACCESS_PATHS = new Set([
	'/dashboard',
	'/clientslist',
	'/horsesclientslist',
	'/stable-details',
	'/room-details',
	'/stables/add',
	'/vetclinicrecordinside',
	'/vetclinicrecordoutside',
	'/reproduction-details',
	'/directinseminationform',
	'/addfarrierrecordinside',
	'/addfarrierrecordoutside',
	'/farrier-medications',
	'/farrier-medication-add',
	'/farrier-medication-edit',
	'/expensespage',
	'/horsefinancecenter',
	'/rolelist',
	'/roles-permissions',
	'/role-permissions',
	'/admin/menu-designer',
	'/userpermissions',
]);

const SETTINGS_CHILD_PATHS = new Set([
	'/access-control',
	'/settings/access-control',
	'/settings/audit-report',
	'/settings/backups',
	'/settings/pricing',
]);

export function orderAccessControlAfterSettings(items: MenuItem[] = []): MenuItem[] {
	const accessItems: MenuItem[] = [];
	const others: MenuItem[] = [];

	(items || []).forEach((item) => {
		const path = normalizeMenuPath(item.path || item.route);
		if (path === '/access-control') accessItems.push(item);
		else others.push(item);
	});

	if (!accessItems.length) return others;

	const settingsIndex = others.findIndex((item) => {
		const path = normalizeMenuPath(item.path || item.route);
		const key = String(item.permission_key || item.text_key || '').toLowerCase();
		return path === '/settings' || key.includes('settings');
	});

	if (settingsIndex >= 0) {
		others.splice(settingsIndex + 1, 0, ...accessItems);
		return others;
	}

	return [...others, ...accessItems];
}

export function prepareMenuItems(items: MenuItem[] = []): MenuItem[] {
	const visible = (items || []).filter((item) => {
		const path = normalizeMenuPath(item.path || item.route);
		return !LEGACY_ACCESS_PATHS.has(path) && !SETTINGS_CHILD_PATHS.has(path);
	});
	return orderAccessControlAfterSettings(visible);
}

// مسارات بوابة المالك السبعة (OwnerPortalShell_Plan_2026-07-12.md §B6) — لها غلاف/شريط علويّ
// مستقلّ (OwnerTopBar.tsx) منذ الفصل، فتُخفى من شريط الإدارة كليًّا (ظهورها هناك سابقًا كان
// عبر filterMenuForOwnerAccount المحذوفة — تعويض/دمج، لا حجب). قائمة ثابتة عمدًا (لا app_menu)
// مطابقة لِـOWNER_NAV_ITEMS في shell/owner/ownerNav.ts، بلا استيراد متبادل بينهما.
const OWNER_PORTAL_PATHS = new Set(['/owner-home', '/my-horses', '/my-horse-stats', '/my-finance', '/my-pregnancy', '/my-milestones']);

export function hideOwnerPortalPaths(items: MenuItem[] = []): MenuItem[] {
	return (items || []).filter((item) => !OWNER_PORTAL_PATHS.has(normalizeMenuPath(item.path || item.route)));
}

export function sortMenuItems(items: MenuItem[]): MenuItem[] {
	return [...items].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}
